import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

export type SupportMode = 'view' | 'edit';
export type SupportSessionStatus = 'active' | 'ended' | 'expired';

/** A change in the vendor's terms (the API's describeSupportAction). */
export interface SupportActionSummary {
    area: string;
    verb: 'create' | 'update' | 'delete' | 'run';
    name?: string;
    action?: string;
    variants?: { created: number; updated: number; deleted: number };
}

export interface SupportSessionAction {
    method: string;
    path: string;
    status?: number;
    at: string;
    summary?: SupportActionSummary;
}

export interface SupportSession {
    _id: string;
    vendor: { _id: string; name?: string } | null;
    startedBy: string;
    mode: SupportMode;
    reason?: string;
    status: SupportSessionStatus;
    startedAt: string;
    expiresAt: string;
    endedAt?: string;
    endedBy?: string;
    actionCount: number;
    /** Changes that went through (actionCount also counts refused ones). */
    changeCount?: number;
    /** Newest first. */
    actions: SupportSessionAction[];
    /** On the production API: a real vendor's live account. */
    production?: boolean;
}

/** What `users/me` adds while support works in a vendor's portal. */
export interface SupportSessionInfo {
    id: string;
    mode: SupportMode;
    expiresAt: string;
    startedBy: string;
}

/** Support sessions: super admins open them; vendors read their history. */
export const useSupportSessionsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    return {
        start: (input: { vendor: string; mode: SupportMode; reason?: string }) =>
            sendAuthGuardedRequest(ApiMethod.POST, routes.supportSessions.base, input) as Promise<SupportSession>,
        tokenFor: (id: string) =>
            sendAuthGuardedRequest(ApiMethod.POST, routes.supportSessions.token(id)) as Promise<{ access_token: string; session: SupportSession }>,
        end: (id: string) => sendAuthGuardedRequest(ApiMethod.POST, routes.supportSessions.end(id)) as Promise<SupportSession>,
        listForVendor: (vendorId: string) =>
            sendAuthGuardedRequest(ApiMethod.GET, routes.supportSessions.forVendor(vendorId)) as Promise<SupportSession[]>,
        /** The vendor's own history (signed in as the vendor, or in a support session). */
        listMine: () => sendAuthGuardedRequest(ApiMethod.GET, routes.supportSessions.mine) as Promise<SupportSession[]>,
        endCurrent: () => sendAuthGuardedRequest(ApiMethod.POST, routes.supportSessions.endCurrent) as Promise<SupportSession>,
    };
};
